import { PUBLIC_ROUTES } from "@/app/router/route-manifest";
import { matchesRouterPath } from "@/app/router/router-path";
import { getDefaultReturnTo, normalizeReturnTo } from "@/core/auth/return-to";

// Public routes a header "Sign in" may return to: those whose path is fixed. A path with a parameter or a
// wildcard can hold a private value (the invoice locator, a legacy encrypted id). Three more kinds of page
// give the default page: the sign-in flow's own pages (returning to /logout would sign the visitor out
// again), and the invitation, recovery and set-password pages, whose state lives in memory or in a
// one-time link that the full-page sign-in round trip loses. `/home/*` is content, so it is allowed by name.
const NO_RETURN_ROUTE_IDS: ReadonlyArray<(typeof PUBLIC_ROUTES)[number]["id"]> = [
  "auth-callback",
  "logout",
  "error",
  "registration-invitation",
  "registration-invitation-recovery",
  "set-password",
];
const RETURNABLE_PATTERNS: string[] = [
  ...PUBLIC_ROUTES.filter(
    (route) =>
      !NO_RETURN_ROUTE_IDS.includes(route.id) &&
      !route.path.includes(":") &&
      !route.path.includes("*"),
  ).map((route) => route.path as string),
  "/home/*",
];

/**
 * The page a header "Sign in" asks to return the visitor to (011-CLMS-241 FR-024): the path of the public
 * page they are on, never its query or fragment, which may hold a private reference. On `/login`, the
 * `returnTo` that page itself uses, as "Continue to sign in" sends it. On any other page, the default page.
 */
export function buildSignInReturnTo(location: Pick<Location, "pathname" | "search">): string {
  if (matchesRouterPath("/login", location.pathname)) {
    return normalizeReturnTo(new URLSearchParams(location.search).get("returnTo"));
  }
  return RETURNABLE_PATTERNS.some((pattern) => matchesRouterPath(pattern, location.pathname))
    ? location.pathname
    : getDefaultReturnTo();
}
